import { observer } from 'mobx-react';
import { FC, ReactElement, memo, useCallback, useMemo } from 'react';
import { IItemsInventoryFilters } from '../../types/itemsInventory';
import { STATUS_FILTER } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import LazyBranchesPicker from 'src/view/components/Controls/Pickers/LazyBranchesPicker/LazyBranchesPicker';
import LazyCategoriesPicker from 'src/view/components/Controls/Pickers/LazyCategoriesPicker/LazyCategoriesPicker';
import Picker from 'src/view/components/Controls/Pickers/Picker/Picker';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import { TBooleanInNumber } from '~/types/main';
import { SearchInput } from '~/view/components/Controls/Inputs';

interface IProps {
  loading?: boolean;
  filters: IItemsInventoryFilters;
  totalItems?: number;
  setFilters: (filters: Partial<IItemsInventoryFilters>) => void;
}

export const ItemsInventoryHeader: FC<IProps> = memo(
  observer((props: IProps): ReactElement | null => {
    const onSearch = useCallback((value: string | undefined) => {
      props.setFilters({ name: value });
    }, []);

    const onChangeStatus = useCallback((options: IPickerOption[]) => {
      let newStatus: TBooleanInNumber | undefined = undefined;

      if (options[0]?.value === STATUS_FILTER.ACTIVE) {
        newStatus = 1;
      } else if (options[0]?.value === STATUS_FILTER.INACTIVE) {
        newStatus = 0;
      }

      props.setFilters({ status: newStatus });
    }, []);

    const onSelectCategory = useCallback((category: IPickerOption[]) => {
      props.setFilters({ category });
    }, []);

    const onSelectBranch = useCallback((branch: IPickerOption[]) => {
      props.setFilters({ branch });
    }, []);

    const statusPickerOptions = useMemo((): IPickerOption[] => {
      return [
        { label: localeStore.t(STATUS_FILTER.ACTIVE), value: STATUS_FILTER.ACTIVE },
        { label: localeStore.t(STATUS_FILTER.INACTIVE), value: STATUS_FILTER.INACTIVE },
      ];
    }, []);

    const statusPickerSelectedOptions = useMemo((): IPickerOption[] => {
      switch (props.filters.status) {
        case 1:
          return [statusPickerOptions[0]];
        case 0:
          return [statusPickerOptions[1]];
        default:
          return [];
      }
    }, [props.filters.status]);

    const needShow: boolean = Boolean(
      props.totalItems || props.loading || props.filters.name || props.filters.status !== undefined || props.filters.category
    );

    if (!needShow) return null;

    return (
      <div className="d-flex flex-row gap-10">
        <SearchInput value={props.filters.name} onChangeValue={onSearch} />
        <Picker
          options={statusPickerOptions}
          selectedOptions={statusPickerSelectedOptions}
          onSelect={onChangeStatus}
          placeholder={localeStore.t('txt_delivery_status_h')}
        />
        <LazyCategoriesPicker searchable selectedOptions={props.filters.category} onSelect={onSelectCategory} />
        <LazyBranchesPicker selectedOptions={props.filters.branch} onSelect={onSelectBranch} searchable hideResetIcon noReset />
      </div>
    );
  })
);
